"use client";

import { cn } from "@guru/shared/utils/cn";
import { Hammer, MessageSquareText, Rocket } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import Link from "next/link";
import { TrialCTA } from "@/components/marketing/trial-cta";
import { ScrollReveal } from "@/components/public/scroll-reveal";

const STEPS = [
  {
    icon: MessageSquareText,
    title: "You ask",
    description: "Most of what's live started as a request from a centre owner like you.",
  },
  {
    icon: Hammer,
    title: "We build in the open",
    description:
      "It lands on this page with an honest status — dates are intentions, not promises.",
  },
  {
    icon: Rocket,
    title: "We ship & write it up",
    description: "Releases go out most weeks, each one explained in plain language on the blog.",
  },
];

/** Twelve weeks of shipping cadence — a quiet ticker under the three steps. */
const WEEKS = Array.from({ length: 12 });

export function HowWeShip() {
  const reduced = !!useReducedMotion();

  return (
    <ScrollReveal threshold={0.2}>
      <section className="relative overflow-hidden rounded-2xl border border-border bg-secondary/30 p-8 sm:p-10">
        <div
          className="ledger-grid ledger-grid-fade pointer-events-none absolute inset-0 opacity-60"
          aria-hidden="true"
        />

        <div className="relative">
          <div className="text-center">
            <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-brand-deep">
              How we ship
            </p>
            <h2 className="text-balance text-2xl font-bold text-foreground sm:text-3xl">
              Built in a loop with the people who use it
            </h2>
          </div>

          {/* Three steps with a connector that pulses across the top */}
          <div className="relative mt-10">
            <div className="absolute inset-x-[16%] top-8 hidden lg:block" aria-hidden="true">
              <div className="relative h-px w-full bg-gradient-to-r from-transparent via-brand/40 to-transparent">
                <motion.span
                  className="absolute -top-[3px] size-1.5 rounded-full bg-brand shadow-[0_0_8px_2px_rgba(245,158,11,0.5)]"
                  initial={false}
                  animate={reduced ? { left: "100%" } : { left: ["0%", "100%"] }}
                  transition={
                    reduced
                      ? { duration: 0 }
                      : {
                          duration: 3.2,
                          repeat: Number.POSITIVE_INFINITY,
                          repeatDelay: 0.6,
                          ease: "easeInOut",
                        }
                  }
                />
              </div>
            </div>

            <ol className="grid gap-8 lg:grid-cols-3">
              {STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <li key={step.title} className="relative flex flex-col items-center text-center">
                    <div className="relative">
                      <span className="relative z-10 flex size-16 items-center justify-center rounded-2xl border border-brand/20 bg-card text-brand-deep shadow-sm">
                        <Icon className="size-7" aria-hidden="true" />
                      </span>
                      <span className="absolute -right-1 -top-1 z-20 flex size-6 items-center justify-center rounded-full bg-brand text-xs font-bold text-brand-contrast ring-4 ring-secondary/30">
                        {i + 1}
                      </span>
                    </div>
                    <h3 className="mt-4 text-base font-semibold text-foreground">{step.title}</h3>
                    <p className="mt-1.5 max-w-xs text-pretty text-sm leading-relaxed text-muted-foreground">
                      {step.description}
                    </p>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Weekly cadence ticker */}
          <div className="mx-auto mt-10 flex max-w-md flex-col items-center gap-2.5">
            <div className="flex items-end gap-1.5">
              {WEEKS.map((_, i) => (
                <motion.span
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length decorative ticker
                  key={i}
                  className="w-2 rounded-full bg-brand"
                  initial={false}
                  animate={
                    reduced
                      ? { height: 14, opacity: 0.5 }
                      : { height: [8, 18, 8], opacity: [0.3, 1, 0.3] }
                  }
                  transition={
                    reduced
                      ? { duration: 0 }
                      : {
                          duration: 1.6,
                          delay: i * 0.12,
                          repeat: Number.POSITIVE_INFINITY,
                          repeatDelay: 0.4,
                          ease: "easeInOut",
                        }
                  }
                  style={{ height: 8 }}
                />
              ))}
            </div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Shipping most weeks
            </p>
          </div>

          <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <TrialCTA placement="roadmap" label="Start 30-day free trial" size="default" />
            <Link
              href="/blog"
              className={cn(
                "text-sm font-medium text-muted-foreground underline-offset-4",
                "hover:text-foreground hover:underline"
              )}
            >
              Read the release notes →
            </Link>
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
